import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import HfpV3ClinicalView from '../../components/hfp/HfpV3ClinicalView';
import { profileSharedWithClinicians, PROFILE_NOT_SHARED_LINE } from '../../utils/profileSharing';

/**
 * RmsHfpV3ClinicalPage
 * --------------------
 * The clinician-facing HFP v3 drill-down (View B, TD-209) at
 * /rms/provider/patient/:patientRecordId/hfp-v3-clinical?domain=&area=.
 *
 * Loads both respondents' HFP v3 form instances and renders the clinical
 * view, which re-derives every rating through the shared engine — the same
 * fixture-pinned rules the backend persists, so this page and the Patient
 * Overview matrix can never disagree.
 */

const HFP_V3_PREFIX = 'hfpV3';
const ROLE_TO_CONTEXT = { guardian: 'home', school_user: 'school' };

// Flatten a stored answers map ({qid: {optionId}} or {qid: value}) to bare
// optionIds, keyed by the engine's item ids ({formKey}:{questionId}).
function collectAnswers(instances) {
  const byContext = { home: {}, school: {} };
  Object.entries(instances).forEach(([instanceId, instanceDoc]) => {
    if (!instanceId.startsWith(HFP_V3_PREFIX)) return;
    const role = Object.keys(ROLE_TO_CONTEXT).find((r) => instanceId.endsWith(`_${r}`));
    if (!role) return;
    const formKey = instanceId.slice(0, -(role.length + 1));
    const context = ROLE_TO_CONTEXT[role];
    const answers = (instanceDoc && instanceDoc.answers) || {};
    Object.entries(answers).forEach(([qid, val]) => {
      const optionId =
        val && typeof val === 'object' ? val.optionId ?? val.value ?? null : val;
      if (optionId != null) byContext[context][`${formKey}:${qid}`] = optionId;
    });
  });
  return byContext;
}

export default function RmsHfpV3ClinicalPage() {
  const { patientRecordId } = useParams();
  const [searchParams] = useSearchParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [childName, setChildName] = useState('');
  const [instances, setInstances] = useState(null);
  // TDI-483: the family decides whether the clinical team sees the profile.
  // null until the record lands; the answers are only subscribed to once it
  // is yes (the rules refuse them otherwise).
  const [profileShared, setProfileShared] = useState(null);

  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    const ref = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    const unsubRecord = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      const first = (data?.subject?.firstName || '').trim();
      if (first) setChildName(first);
      setProfileShared(profileSharedWithClinicians(data));
    }, () => setProfileShared(false));
    return () => unsubRecord();
  }, [orgId, patientRecordId]);

  useEffect(() => {
    if (!orgId || !patientRecordId || profileShared !== true) return undefined;
    const unsubInstances = onSnapshot(
      collection(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId, 'formInstances'),
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          next[d.id] = d.data();
        });
        setInstances(next);
      },
      () => setInstances({})
    );
    return () => unsubInstances();
  }, [orgId, patientRecordId, profileShared]);

  const answersByContext = useMemo(
    () => collectAnswers(instances || {}),
    [instances]
  );
  const hasAnyAnswers =
    Object.keys(answersByContext.home).length > 0 ||
    Object.keys(answersByContext.school).length > 0;

  const breadcrumbs = [
    { label: 'Home', path: '/rms/provider/dashboard' },
    { label: 'Patient Overview', path: `/rms/provider/patient/${patientRecordId}` },
    { label: 'Clinical HFP', path: null },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />
      <div className="flex-1 px-6 sm:px-8 lg:px-12 py-6">
        {childName && (
          <h1 className="text-lg font-bold mb-4" style={{ color: '#43213e' }}>
            {childName} — Clinical Holistic Functional Profile
          </h1>
        )}
        {profileShared === false ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center" role="status">
            <h2 className="text-base font-semibold text-gray-900">Strengths and needs profile: not shared</h2>
            <p className="text-sm text-gray-600 mt-2">{PROFILE_NOT_SHARED_LINE}</p>
          </div>
        ) : instances === null ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
            Loading the profile…
          </div>
        ) : !hasAnyAnswers ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
            No HFP v3 answers exist for this record yet.
          </div>
        ) : (
          <HfpV3ClinicalView
            answersByContext={answersByContext}
            initialDomain={searchParams.get('domain') || undefined}
            initialArea={searchParams.get('area') || undefined}
          />
        )}
      </div>

      {/* Disclaimer footer (always visible) — same wording as every other HFP
          surface: the profile is never a diagnosis. */}
      <footer className="flex-shrink-0 border-t border-gray-200 bg-white px-6 sm:px-8 lg:px-12 py-3">
        <p className="text-[11px] leading-relaxed text-gray-500 text-center max-w-4xl mx-auto">
          This is not a medical assessment and does not constitute a diagnosis, and cannot be
          used to claim or deny any medical condition.
        </p>
      </footer>
    </div>
  );
}
